import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Download, Save, AlertTriangle, Users, Layers } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const save = (data, name, type) => {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const cash = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const btn = "hard-btn py-2 px-4 font-mono uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40";
const outline = "border-2 border-ink py-2 px-4 font-mono uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-2 hover:bg-ink hover:text-white disabled:opacity-40";

export function YearEndCard({ year, enabled }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try { setData((await api.get(`/reports/yearend/preview?year=${year}`)).data); }
      catch { setData(null); }
    })();
  }, [year]);

  const pull = async (label, path, body, name, type) => {
    setBusy(label);
    try {
      const res = body
        ? await api.post(path, body, { responseType: "blob" })
        : await api.get(path, { responseType: "blob" });
      save(res.data, name, type);
      toast.success(`${label} ready`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  if (!data) return null;
  const w2 = data.w2;
  const nec = data.form_1099;
  // A year with no W-2 employees and no contractors has nothing to file.
  if (!w2.employees && !nec.contractors) return null;

  return (
    <div className="hard-card p-6 mb-8" data-testid="yearend-card">
      <div className="overline mb-2">§ YEAR-END STATEMENTS · {year}</div>
      <div className="font-heading font-black text-2xl tracking-tight">
        W-2 · W-3 · 1099-NEC
      </div>
      <div className="font-mono text-xs text-muted-foreground mt-1 max-w-2xl">
        Employee and recipient copies are filled on the official IRS templates and are meant to be
        printed and handed out. Filing happens electronically — the EFW2 wage file goes to the SSA,
        the IRIS file to the IRS.
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="border-2 border-ink p-4" data-testid="w2-block">
          <div className="overline mb-2 text-[10px]">Form W-2 · {w2.employees} employee(s)</div>
          <div className="font-mono text-xs space-y-1" data-testid="w2-totals">
            <div className="flex justify-between"><span>Box 1 wages</span><span>{cash(w2.totals.box1)}</span></div>
            <div className="flex justify-between"><span>Box 2 federal withheld</span><span>{cash(w2.totals.box2)}</span></div>
            <div className="flex justify-between"><span>Box 3 SS wages</span><span>{cash(w2.totals.box3)}</span></div>
            <div className="flex justify-between"><span>Box 5 Medicare wages</span><span>{cash(w2.totals.box5)}</span></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button disabled={!enabled || !w2.employees} data-testid="w2-employee-copies"
              onClick={() => pull("Employee copies", "/reports/w2.pdf", { year: Number(year) },
                `w2-${year}-employee-copies.pdf`, "application/pdf")}
              className={btn}><Download className="w-3 h-3" />
              {busy === "Employee copies" ? "Building…" : "Employee copies B/C/2"}
            </button>
            <button disabled={!enabled || !w2.employees} data-testid="w2-record-copies"
              onClick={() => pull("Record set", "/reports/w2.pdf",
                { year: Number(year), copies: "A,D" },
                `w2-${year}-record.pdf`, "application/pdf")}
              className={outline}><FileText className="w-3 h-3" /> Record set + W-3
            </button>
            <button disabled={!enabled || !w2.employees} data-testid="efw2-download"
              onClick={() => pull("EFW2 file", `/reports/efw2.txt?year=${year}`, null,
                `w2report-${year}.txt`, "text/plain")}
              className={btn}><Download className="w-3 h-3" /> EFW2 file for SSA
            </button>
          </div>
          {w2.missing_ssn.length > 0 && (
            <div className="mt-3 font-mono text-[10px] text-red-700 flex gap-2" data-testid="w2-missing-ssn">
              <AlertTriangle className="w-3 h-3 shrink-0 mt-[2px]" />
              No SSN on file for {w2.missing_ssn.join(", ")} — the SSA will reject those records.
            </div>
          )}
          {(w2.itin_on_w2 || []).length > 0 && (
            <div className="mt-3 font-mono text-[10px] text-red-700 flex gap-2" data-testid="w2-itin-warning">
              <AlertTriangle className="w-3 h-3 shrink-0 mt-[2px]" />
              {w2.itin_on_w2.join(", ")} hold an ITIN — an ITIN cannot be used for W-2 wage
              reporting. Set them to 1099 filing, or record an SSN.
            </div>
          )}
          {!data.submitter_ready && (
            <div className="mt-2 font-mono text-[10px]" data-testid="w2-submitter-warning">
              Add your SSA BSO User ID below before uploading — it is your signature on the file.
            </div>
          )}
        </div>

        <div className="border-2 border-ink p-4" data-testid="nec-block">
          <div className="overline mb-2 text-[10px]">
            Form 1099-NEC · {nec.reportable} of {nec.contractors} contractor(s)
          </div>
          <div className="font-mono text-xs space-y-1" data-testid="nec-totals">
            <div className="flex justify-between"><span>Box 1 compensation</span><span>{cash(nec.total_compensation)}</span></div>
            <div className="flex justify-between"><span>Box 4 federal withheld</span><span>{cash(nec.total_federal_withheld)}</span></div>
            <div className="flex justify-between opacity-70">
              <span>Below the {cash(nec.threshold)} threshold</span><span>{nec.below_threshold}</span>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button disabled={!enabled || !nec.reportable} data-testid="nec-recipient-copies"
              onClick={() => pull("Recipient copies", "/reports/1099nec.pdf", { year: Number(year) },
                `1099nec-${year}-recipient-copies.pdf`, "application/pdf")}
              className={btn}><Download className="w-3 h-3" /> Recipient copies B/2
            </button>
            <button disabled={!enabled || !nec.reportable} data-testid="nec-record-copies"
              onClick={() => pull("1099 record set", "/reports/1099nec.pdf",
                { year: Number(year), copies: "A" },
                `1099nec-${year}-record.pdf`, "application/pdf")}
              className={outline}><FileText className="w-3 h-3" /> Record set + 1096
            </button>
            <button disabled={!enabled || !nec.reportable} data-testid="iris-download"
              onClick={() => pull("IRIS file", `/reports/1099-iris.csv?year=${year}`, null,
                `iris-1099nec-${year}.csv`, "text/csv")}
              className={btn}><Download className="w-3 h-3" /> IRIS file for IRS
            </button>
            <button disabled={!enabled || !nec.reportable} data-testid="nec-bulk-zip"
              onClick={() => pull("Bulk 1099 export", "/reports/1099nec-bulk.zip",
                { year: Number(year) },
                `1099nec-bulk-${year}.zip`, "application/zip")}
              className={outline}><Layers className="w-3 h-3" /> Bulk export (ZIP)
            </button>
          </div>
          <div className="mt-2 font-mono text-[10px] text-muted-foreground" data-testid="nec-bulk-note">
            One PDF per payee, sorted into <b>business/</b> and <b>individual/</b> folders, with the
            Copy A + 1096 record set and a manifest CSV.
          </div>
          {nec.missing_tin.length > 0 && (
            <div className="mt-3 font-mono text-[10px] text-red-700" data-testid="nec-missing-tin">
              No TIN on file for {nec.missing_tin.join(", ")}.
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 font-mono text-[10px] border-2 border-ink p-3" data-testid="copy-a-warning">
        <AlertTriangle className="w-3 h-3 inline mr-1" />{data.copy_a_warning}
      </div>
    </div>
  );
}

export function SubmitterCard() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { setForm((await api.get("/settings/submitter")).data); } catch { setForm(null); }
    })();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      await api.put("/settings/submitter", form);
      toast.success("Submitter details saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!form) return null;
  const field = (k, label, ph = "") => (
    <div>
      <div className="overline mb-2 text-[10px]">{label}</div>
      <input data-testid={`submitter-${k}`} value={form[k] || ""} onChange={set(k)}
        placeholder={ph} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
    </div>
  );

  return (
    <div className="hard-card p-6 mb-8" data-testid="submitter-card">
      <div className="overline mb-2">§ SSA / IRS SUBMITTER</div>
      <div className="font-heading font-black text-2xl tracking-tight">Who files the wage file</div>
      <div className="font-mono text-xs text-muted-foreground mt-1 max-w-2xl">
        Your Business Services Online User ID is your electronic signature on the EFW2 file — the
        SSA rejects a file without it. Register once at ssa.gov/bso, then test the file through
        AccuWage Online before uploading.
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {field("bso_user_id", "BSO User ID", "8 characters")}
        {field("contact_name", "Contact name")}
        {field("contact_phone", "Contact phone")}
        {field("contact_email", "Contact email")}
        {field("state_tax_id", "Employer state ID (W-2 box 15)")}
        {field("ein", "Submitter EIN", "if different from the company")}
      </div>
      <button onClick={submit} disabled={busy} data-testid="submitter-save"
        className={`${btn} mt-4`}>
        <Save className="w-3 h-3" /> {busy ? "Saving…" : "Save submitter details"}
      </button>
      <div className="mt-3 font-mono text-[10px] opacity-70 flex gap-2">
        <Users className="w-3 h-3 shrink-0 mt-[2px]" />
        10 or more W-2s or 1099s in a year must be filed electronically.
      </div>
    </div>
  );
}
